import { OpenAPI } from "../../services";

const validateUrlBasic = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    console.error('Invalid URL provided to ComposeURLWithGetQueryArguments:', url);
    return false;
  }
  
  if (url.length > 2000) {
    console.error('URL too long:', url);
    return false;
  }
  
  return true;
};

const validateUrlFormat = (url: string): boolean => {
  if (url.includes('http://') || url.includes('https://')) {
    console.error('Malformed URL detected:', url);
    return false;
  }
  
  if (url.includes('//') && !url.startsWith('//')) {
    console.error('Double slash detected in URL:', url);
    return false;
  }
  
  return true;
};

const hasSuspiciousNumberPattern = (url: string): boolean => {
  return /\/[a-zA-Z]+\d{6,}/.test(url);
};

const hasSuspiciousProtocolPattern = (url: string): boolean => {
  const suspiciousPatterns = [
    /\/https\d+/, /\/http\d+/, /\/ftp\d+/, /\/file\d+/,
    /\/data\d+/, /\/javascript\d+/, /\/vbscript\d+/,
    /\/mailto\d+/, /\/tel\d+/, /\/sms\d+/
  ];
  return suspiciousPatterns.some(pattern => pattern.test(url));
};

const hasSuspiciousCharacterSequence = (url: string): boolean => {
  return /[a-zA-Z]{3,}\d{3,}[a-zA-Z]*\d{3,}/.test(url);
};

const hasPathTraversal = (url: string): boolean => {
  return url.includes('../') || url.includes('..\\');
};

const hasControlCharacters = (url: string): boolean => {
  // eslint-disable-next-line no-control-regex
  return /[\x00-\x1F\x7F]/.test(url);
};

const SECURITY_CHECKS: Array<{ check: (url: string) => boolean; message: string }> = [
  { check: hasSuspiciousNumberPattern, message: 'Suspicious URL pattern detected:' },
  { check: hasSuspiciousProtocolPattern, message: 'Suspicious protocol pattern detected:' },
  { check: hasSuspiciousCharacterSequence, message: 'Suspicious character sequence detected:' },
  { check: hasPathTraversal, message: 'Path traversal detected:' },
  { check: hasControlCharacters, message: 'Control characters detected in URL:' },
];

const validateUrlSecurity = (url: string): boolean => {
  const failed = SECURITY_CHECKS.find(({ check }) => check(url));
  if (failed) {
    console.error(failed.message, url);
    return false;
  }
  return true;
};

const normalizeUrl = (url: string): string => {
  if (!url.startsWith('/')) {
    url = '/' + url;
  }
  return url.replace(/\/+/g, '/');
};

const buildQueryString = (queryParams?: Record<string, string>): string => {
  if (queryParams && Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
      searchParams.set(key, value);
    }
    return '?' + searchParams.toString();
  }
  
  const currentQueryString = window.location.search;
  return currentQueryString || '';
};

export const ComposeURLWithGetQueryArguments = (url: string, queryParams?: Record<string, string>): string => {
  if (!validateUrlBasic(url) || !validateUrlFormat(url) || !validateUrlSecurity(url)) {
    return '/';
  }
  
  const normalizedUrl = normalizeUrl(url);
  const queryString = buildQueryString(queryParams);
  
  return normalizedUrl + queryString;
};

const handleJwtToken = async (value: string): Promise<string> => {
  if (!value.startsWith('eyJ')) {
    return value;
  }

  console.log('[ComposeURLWithResponseParameters] JWT token detected in code parameter');
  console.log('[ComposeURLWithResponseParameters] JWT token (first 50 chars):', value.substring(0, 50) + '...');
  
  try {
    const { JwtAuthHandler } = await import('@shared/utils/auth/JwtAuthHandler');
    
    if (JwtAuthHandler.needsToWait(value)) {
      console.log('[ComposeURLWithResponseParameters] JWT token has nbf claim, waiting for it to become valid...');
      await JwtAuthHandler.waitForTokenToBeValid(value);
      console.log('[ComposeURLWithResponseParameters] JWT token is now valid, proceeding with redirect');
    }
  } catch (error) {
    console.error('[ComposeURLWithResponseParameters] Error handling JWT token nbf claim:', error);
  }
  
  return value;
};

const buildSearchParams = async (parameters: Record<string, any>): Promise<URLSearchParams> => {
  const searchParams = new URLSearchParams();

  for (const key of Object.keys(parameters)) {
    const value = parameters[key];
    
    if (key === 'code' && typeof value === 'string') {
      const processedValue = await handleJwtToken(value);
      searchParams.set(key, processedValue);
    } else {
      searchParams.set(key, value);
    }
  }

  return searchParams;
};

export const ComposeURLWithResponseParameters = async (redirectionResponse: { location: string; parameters?: Record<string, any> }): Promise<string> => {
  const baseUrl = OpenAPI.BASE;
  const location = redirectionResponse.location;
  
  let fullUrl: URL;
  if (location.startsWith('http://') || location.startsWith('https://')) {
    fullUrl = new URL(location);
  } else {
    fullUrl = new URL(baseUrl.replace(/\/+$/, '') + '/' + location.replace(/^\/+/, ''));
  }

  if (redirectionResponse.parameters) {
    const searchParams = await buildSearchParams(redirectionResponse.parameters);
    fullUrl.search = searchParams.toString();
  }

  return fullUrl.toString();
};
